/**
 * App Loader — Dynamic module import, CSS injection, and app lifecycle.
 */

const loadedCss = new Set();
const loadedModules = {};
const pendingLoads = {};  // dedup concurrent loads of the same appId

/**
 * Inject an app's CSS file if not already loaded.
 */
function injectCss(cssPath) {
  if (loadedCss.has(cssPath)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = cssPath;
  link.dataset.appCss = cssPath;
  document.head.appendChild(link);
  loadedCss.add(cssPath);
}

/**
 * Dynamically import an app module. Caches after first load.
 * De-duplicates concurrent import calls for the same appId.
 */
async function importAppModule(appId) {
  if (loadedModules[appId]) return loadedModules[appId];
  if (pendingLoads[appId]) return pendingLoads[appId];
  // Vite analyzes the dynamic import pattern at build time and code-splits
  // each app/<id>/app.js into its own chunk. Asset fingerprinting replaces
  // the old `?v=18` cache-busting query string.
  pendingLoads[appId] = import(`../apps/${appId}/app.js`)
    .then(mod => { loadedModules[appId] = mod; delete pendingLoads[appId]; return mod; })
    .catch(err => { delete pendingLoads[appId]; throw err; });
  return pendingLoads[appId];
}

/**
 * Load and render an app.
 * @param {object} manifest - The app's manifest object
 * @param {string} view - The view to render (or manifest.defaultView)
 * @param {HTMLElement} container - DOM container
 * @param {object} context - Platform context
 * @returns {object} The loaded module
 */
export async function loadApp(manifest, view, container, context) {
  // Inject CSS
  if (manifest.css) {
    injectCss(manifest.css);
  }

  // Import module
  const mod = await importAppModule(manifest.id);

  // Render
  const targetView = view || manifest.defaultView;
  if (mod.render) {
    await mod.render(container, context, targetView);
  }

  return mod;
}

/**
 * Navigate within a currently loaded app.
 */
export async function navigateApp(appId, view, container, context) {
  const mod = loadedModules[appId];
  if (!mod) return;

  if (mod.navigateTo) {
    await mod.navigateTo(view, container, context);
  }
}

/**
 * Destroy/cleanup the current app before switching.
 */
export function destroyApp(appId) {
  const mod = loadedModules[appId];
  if (mod && mod.destroy) {
    mod.destroy();
  }

  // Defensive cleanup: any app-specific role-preview label (#viewingAsRole)
  // must not leak into the next app. Apps SHOULD clear this in their own
  // destroy(), but if they forget, this catches it.
  const viewingEl = document.getElementById("viewingAsRole");
  if (viewingEl) {
    viewingEl.textContent = "";
    viewingEl.style.display = "none";
  }
}
